/**
 * Forking a template into a customized-assessment draft, and turning a
 * draft into something a template can take.
 *
 * A template the workspace owns is forked from its live document and
 * reverted to its published version, so its ids stay shared with the working
 * copy and the draft can be rebased onto it later (see draft-rebase). Any
 * other template (built-in, or shared by another workspace) is rebuilt from
 * its questions and can only become a new template.
 */

import * as buffer from 'lib0/buffer';
import {
  Doc as YDoc,
  applyUpdate,
  encodeSnapshot,
  encodeStateAsUpdate,
  snapshot,
} from 'yjs';

import { DEFAULT_ORG_DETAIL_VIEWS } from 'js/utils/org-detail-views';
import { randomBase32String } from 'js/utils/string';
import { buildTemplateDoc } from 'js/utils/template-doc';
import { revertToPublished } from 'js/utils/draft-rebase';
import {
  assessmentPayloadFromTemplate,
  isLiveTemplate,
} from 'js/utils/workspace-template';

export const { toBase64, fromBase64 } = buffer;

/** Workspace templates arrive as subscriptions; built-ins do not. */
export function isOwnedTemplate(template) {
  return template?.access_type === 'owned' && !!template?.template_id;
}

/**
 * What a draft is "of": the template's lineage key, unique across workspaces.
 * The same person customizing the same template for the same team picks
 * their draft back up, and a one-off launch keeps the template's key, so its
 * results line up with the template's other runs.
 */
export function draftSourceKey(template) {
  return template.key;
}

export function draftType(template) {
  return isLiveTemplate(template) ? 'live' : 'survey';
}

/**
 * An editor-made template keeps N/A on its answer sets, not among a
 * question's answers, so a published version's questions don't show it.
 * Adds it back as the answer the converter recognizes.
 */
function withNotApplicable(questionSets, answerSets = {}) {
  return questionSets.map((questionSet) => ({
    ...questionSet,
    questions: questionSet.questions.map((question) => {
      const answerSet = answerSets[question.answerSetId];
      const answers = question.answers ?? [];

      if (!answerSet?.includeNA || answers.some((answer) => answer.is_na)) {
        return question;
      }

      return {
        ...question,
        answers: [
          ...answers,
          { value: null, text: answerSet.naText || 'N/A', is_na: true },
        ],
      };
    }),
  }));
}

/**
 * Rebuilds a template from its published questions: the ones still selected
 * on the configure step, in the editor's document format. For a template
 * the workspace doesn't own; its draft can't go back to it.
 */
export function forkFromQuestions(template, questionSets) {
  const payload = assessmentPayloadFromTemplate(template);
  const doc = buildTemplateDoc({
    type: draftType(template),
    assessment: {
      ...payload,
      question_sets: withNotApplicable(
        questionSets,
        template.assessment?.answerSets
      ),
    },
    config: template.config,
    name: template.name,
    description: template.description,
  });

  return {
    initialUpdate: toBase64(encodeStateAsUpdate(doc)),
    baseline: null,
  };
}

/**
 * Forks a workspace template's live document (`stateUpdate`, base64) and
 * turns it back into the template's last published version (`published`, a
 * revision's assessment, and its `config`), so edits still unpublished in the
 * editor don't come along. The published version is the draft's baseline:
 * updating the template later applies what the draft changed relative to it.
 * Questions turned off on the configure step are such changes.
 */
export function forkFromTemplateState(
  stateUpdate,
  excludedKeys = new Set(),
  { published = null, config = null } = {}
) {
  const doc = new YDoc();
  applyUpdate(doc, fromBase64(stateUpdate));

  const baseline = published ? { assessment: published, config } : null;

  if (baseline) {
    revertToPublished(doc, baseline);
  }

  if (excludedKeys.size > 0) {
    const elements = doc.getMap('elements');
    const pages = doc.getMap('pages');
    const pageOf = (element) =>
      element.get('position')?.get('pageElements')?.get('t');

    doc.transact(() => {
      const excluded = [...elements.entries()].filter(([, element]) =>
        excludedKeys.has(element.get('key'))
      );
      // Read before deleting: a deleted element's fields read as undefined
      const emptied = new Set(excluded.map(([, element]) => pageOf(element)));
      excluded.forEach(([guid]) => elements.delete(guid));

      // A question set with every question turned off goes too, as it does
      // on the configure step
      [...elements.values()].forEach((element) =>
        emptied.delete(pageOf(element))
      );
      emptied.forEach((pageGuid) => pages.delete(pageGuid));
    });
  }

  return {
    initialUpdate: toBase64(encodeStateAsUpdate(doc)),
    baseline,
  };
}

/** The snapshot a revision records of `doc`. */
export function snapshotOf(doc) {
  return toBase64(encodeSnapshot(snapshot(doc)));
}

/**
 * Revision fields for publishing, the same ones the editors send. `doc` is
 * the document being published, or pass `crdtSnapshot` when publishing a
 * document this page doesn't hold (a template being merged into).
 */
export function revisionFor(assessment, { doc, crdtSnapshot, name } = {}) {
  return {
    version_id: randomBase32String(10),
    name: name ?? assessment.meta.name,
    description: assessment.meta.description,
    is_configurable: true,
    configuration: assessment.config,
    views: { orgDetail: DEFAULT_ORG_DETAIL_VIEWS },
    crdt_snapshot: crdtSnapshot ?? toBase64(encodeSnapshot(snapshot(doc))),
    snapshot_version: 'v1',
  };
}
